import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, tap } from 'rxjs/operators';
import { AppAuthService } from '../../auth/services/app-auth.service';
import { CurrentMemberProvider } from '../../auth/services/current-member-provider.service';
import { InternalMember } from '../models/member/internal-member.model';
import { Member } from '../models/member/member.model';
import { ExchangeApiService } from './exchange-api.service';

@Injectable()
export class MemberService {

  loggedInSubject = new BehaviorSubject<boolean>(false);
  currentUserSubject = new BehaviorSubject<Member | null>(null);

  public loggedIn: Observable<boolean> = this.loggedInSubject.asObservable();
  public currentUser: Observable<Member> = this.currentUserSubject.asObservable().pipe(
    filter(cu => null !== cu),
    distinctUntilChanged(),
  );

  constructor(
    private apiService: ExchangeApiService,
    private appAuth: AppAuthService,
    private currentMemberProvider: CurrentMemberProvider,
  ) {
  }

  /**
   * Purge the current user from the app
   */
  userLogout(): void {
    this.currentUserSubject.next(null);
    this.loggedInSubject.next(false);

    this.appAuth.logout();
  }

  fetchCurrentUser(): Observable<Member> {
    return this.currentMemberProvider.get().pipe(
      catchError(() => this.createCurrentMember()),
      tap(user => {
        this.currentUserSubject.next(user);
        this.loggedInSubject.next(true);
      }),
    );
  }

  /**
   * Register an internal member
   *
   * @param member Member to register
   */
  registerInternal(member): Observable<InternalMember> {
    return this.apiService.post(`internal_members`, member).pipe(
      map(data => new Member(data)),
    );
  }

  /**
   * Register the current member as an internal member
   */
  createCurrentMember(): Observable<InternalMember> {
    const uuid = this.currentMemberProvider.getUuid();

    return this.registerInternal({id: uuid});
  }
}
